import type {
  PreVisitQuestion,
  PreVisitQuestionDraft,
  PreVisitQuestionOption,
} from '@/api/preVisit/preVisitApi.types';
import type { PreVisitQuestionType } from '@/constants/preVisitQuestionTypes';
import { isPreVisitChoiceQuestionType } from '@/constants/preVisitQuestionTypes';
import { isBlank } from '@/utils/formValidation';

export interface EditableOptionRow {
  key: string;
  code: string | null;
  labelEn: string;
  labelPl: string;
}

export interface EditableQuestionRow {
  key: string;
  code: string | null;
  questionType: PreVisitQuestionType;
  labelEn: string;
  labelPl: string;
  helpEn: string;
  helpPl: string;
  options: EditableOptionRow[];
  isRequired: boolean;
}

let questionRowKeyCounter = 0;
function nextQuestionRowKey(): string {
  questionRowKeyCounter = questionRowKeyCounter + 1;
  return 'pre-visit-question-row-' + questionRowKeyCounter;
}

let optionRowKeyCounter = 0;
function nextOptionRowKey(): string {
  optionRowKeyCounter = optionRowKeyCounter + 1;
  return 'pre-visit-option-row-' + optionRowKeyCounter;
}

function toEditableOptionRow(option: PreVisitQuestionOption): EditableOptionRow {
  return {
    key: nextOptionRowKey(),
    code: option.code,
    labelEn: option.labelEn,
    labelPl: option.labelPl,
  };
}

export function buildNewOptionRow(): EditableOptionRow {
  return { key: nextOptionRowKey(), code: null, labelEn: '', labelPl: '' };
}

export function toEditableQuestionRow(question: PreVisitQuestion): EditableQuestionRow {
  return {
    key: nextQuestionRowKey(),
    code: question.code,
    questionType: question.questionType,
    labelEn: question.labelEn,
    labelPl: question.labelPl,
    helpEn: question.helpEn === null ? '' : question.helpEn,
    helpPl: question.helpPl === null ? '' : question.helpPl,
    options: question.options.map(toEditableOptionRow),
    isRequired: question.isRequired,
  };
}

export function toEditableQuestionRows(questions: PreVisitQuestion[]): EditableQuestionRow[] {
  return questions
    .filter(function onlyActive(question) {
      return question.isActive;
    })
    .map(toEditableQuestionRow);
}

export function buildRemovedQuestions(questions: PreVisitQuestion[]): PreVisitQuestion[] {
  return questions.filter(function onlyInactive(question) {
    return question.isActive === false;
  });
}

export function buildNewQuestionRow(questionType: PreVisitQuestionType): EditableQuestionRow {
  return {
    key: nextQuestionRowKey(),
    code: null,
    questionType,
    labelEn: '',
    labelPl: '',
    helpEn: '',
    helpPl: '',
    options: isPreVisitChoiceQuestionType(questionType)
      ? [buildNewOptionRow(), buildNewOptionRow()]
      : [],
    isRequired: false,
  };
}

export function moveQuestionRow(
  rows: EditableQuestionRow[],
  index: number,
  direction: -1 | 1
): EditableQuestionRow[] {
  const targetIndex = index + direction;
  if (targetIndex < 0 || targetIndex >= rows.length) {
    return rows;
  }
  const reordered = [...rows];
  const movedRow = reordered[index];
  const displacedRow = reordered[targetIndex];
  if (movedRow === undefined || displacedRow === undefined) {
    return rows;
  }
  reordered[index] = displacedRow;
  reordered[targetIndex] = movedRow;
  return reordered;
}

export function isQuestionRowValid(row: EditableQuestionRow): boolean {
  if (isBlank(row.labelEn) || isBlank(row.labelPl)) {
    return false;
  }
  if (isPreVisitChoiceQuestionType(row.questionType)) {
    const filledOptions = row.options.filter(function isFilled(option) {
      return isBlank(option.labelEn) === false && isBlank(option.labelPl) === false;
    });
    return filledOptions.length >= 2;
  }
  return true;
}

export function toQuestionDraft(row: EditableQuestionRow): PreVisitQuestionDraft {
  return {
    code: row.code,
    questionType: row.questionType,
    labelEn: row.labelEn.trim(),
    labelPl: row.labelPl.trim(),
    helpEn: isBlank(row.helpEn) ? null : row.helpEn.trim(),
    helpPl: isBlank(row.helpPl) ? null : row.helpPl.trim(),
    options: row.options
      .filter(function isFilled(option) {
        return isBlank(option.labelEn) === false && isBlank(option.labelPl) === false;
      })
      .map(function toOptionDraft(option) {
        return {
          code: option.code,
          labelEn: option.labelEn.trim(),
          labelPl: option.labelPl.trim(),
        };
      }),
    isRequired: row.isRequired,
  };
}

export function serializeQuestionRowsForComparison(rows: EditableQuestionRow[]): string {
  return JSON.stringify(
    rows.map(function withoutRenderKeys(row) {
      return {
        code: row.code,
        questionType: row.questionType,
        labelEn: row.labelEn,
        labelPl: row.labelPl,
        helpEn: row.helpEn,
        helpPl: row.helpPl,
        isRequired: row.isRequired,
        options: row.options.map(function optionWithoutRenderKey(option) {
          return { code: option.code, labelEn: option.labelEn, labelPl: option.labelPl };
        }),
      };
    })
  );
}
